import { useState, useMemo, useCallback, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnOrderState,
  type ColumnSizingState,
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ArrowUpDown, ArrowUp, ArrowDown, Columns3, Filter, GripVertical } from "lucide-react";

// ── Persistence helpers ──────────────────────────────────────────────────

function loadPrefs(tableId: string) {
  try {
    const raw = localStorage.getItem(`dt_prefs_${tableId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePrefs(
  tableId: string,
  prefs: {
    columnSizing?: ColumnSizingState;
    columnOrder?: ColumnOrderState;
    columnVisibility?: VisibilityState;
  }
) {
  try {
    localStorage.setItem(`dt_prefs_${tableId}`, JSON.stringify(prefs));
  } catch {}
}

// ── Sortable header cell ─────────────────────────────────────────────────

function SortableHeader({
  id,
  children,
  canReorder,
}: {
  id: string;
  children: React.ReactNode;
  canReorder: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!canReorder) return <>{children}</>;

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1" {...attributes}>
      <span {...listeners} className="cursor-grab text-muted-foreground hover:text-foreground">
        <GripVertical className="h-3 w-3" />
      </span>
      {children}
    </div>
  );
}

// ── DataTable Props ──────────────────────────────────────────────────────

export interface DataTableProps<TData> {
  tableId: string;
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
  enableFiltering?: boolean;
  enableResize?: boolean;
  enableReorder?: boolean;
  enableColumnToggle?: boolean;
  enableSorting?: boolean;
  globalFilter?: string;
  rowClassName?: (row: TData) => string;
  renderRowWrapper?: (row: TData, children: React.ReactNode) => React.ReactNode;
}

// ── DataTable Component ──────────────────────────────────────────────────

export function DataTable<TData>({
  tableId,
  columns,
  data,
  isLoading = false,
  emptyMessage = "No data found.",
  onRowClick,
  enableFiltering = true,
  enableResize = true,
  enableReorder = true,
  enableColumnToggle = true,
  enableSorting = true,
  globalFilter: externalGlobalFilter,
  rowClassName,
  renderRowWrapper,
}: DataTableProps<TData>) {
  // Load persisted preferences
  const savedPrefs = useMemo(() => loadPrefs(tableId), [tableId]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    savedPrefs?.columnVisibility ?? {}
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    savedPrefs?.columnOrder ?? []
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    savedPrefs?.columnSizing ?? {}
  );
  const [showFilters, setShowFilters] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");

  // Persist preferences on change
  useEffect(() => {
    savePrefs(tableId, { columnSizing, columnOrder, columnVisibility });
  }, [tableId, columnSizing, columnOrder, columnVisibility]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      columnSizing,
      globalFilter: externalGlobalFilter ?? globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    columnResizeMode: "onChange",
    enableColumnResizing: enableResize,
    enableSorting,
  });

  // DnD sensors for column reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const allCols = table.getAllLeafColumns().map((c) => c.id);
      const currentOrder = columnOrder.length > 0 ? columnOrder : allCols;
      const oldIndex = currentOrder.indexOf(active.id as string);
      const newIndex = currentOrder.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      setColumnOrder(arrayMove(currentOrder, oldIndex, newIndex));
    },
    [columnOrder, table]
  );

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const leafColumnIds = table.getAllLeafColumns().map((c) => c.id);

  if (isLoading) {
    return <p className="p-4 text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-2 w-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {enableFiltering && (
          <>
            <Input
              placeholder="Search all columns…"
              value={externalGlobalFilter ?? globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="max-w-xs h-8 text-sm"
            />
            <Button
              variant={showFilters ? "secondary" : "outline"}
              size="sm"
              className="h-8"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-3.5 w-3.5 mr-1" />
              Filters
            </Button>
          </>
        )}
        {enableColumnToggle && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 ml-auto">
                <Columns3 className="h-3.5 w-3.5 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 max-h-72 overflow-auto">
              {table.getAllLeafColumns().map((column) => {
                if (column.id === "actions") return null;
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Table */}
      <div className="relative w-full overflow-auto rounded-md border">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table
            className="w-full caption-bottom text-sm"
            style={{ minWidth: table.getTotalSize() }}
          >
            <thead className="[&_tr]:border-b">
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b transition-colors">
                  <SortableContext
                    items={columnOrder.length > 0 ? columnOrder : leafColumnIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="h-10 px-3 text-left align-middle font-medium text-muted-foreground text-xs relative select-none"
                        style={{ width: header.getSize() }}
                      >
                        <SortableHeader id={header.id} canReorder={enableReorder}>
                          {header.isPlaceholder ? null : (
                            <div
                              className={cn(
                                "flex items-center gap-1",
                                header.column.getCanSort() && "cursor-pointer hover:text-foreground"
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {enableSorting && header.column.getCanSort() && (
                                <span className="ml-0.5">
                                  {header.column.getIsSorted() === "asc" ? (
                                    <ArrowUp className="h-3 w-3" />
                                  ) : header.column.getIsSorted() === "desc" ? (
                                    <ArrowDown className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 opacity-30" />
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                        </SortableHeader>

                        {/* Column resize handle */}
                        {enableResize && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn(
                              "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
                              header.column.getIsResizing()
                                ? "bg-primary"
                                : "hover:bg-muted-foreground/30"
                            )}
                          />
                        )}
                      </th>
                    ))}
                  </SortableContext>
                </tr>
              ))}

              {/* Column filter row */}
              {showFilters && enableFiltering && (
                <tr className="border-b bg-muted/30">
                  {headerGroups[0]?.headers.map((header) => (
                    <th key={`filter-${header.id}`} className="px-3 py-1.5">
                      {header.column.getCanFilter() ? (
                        <Input
                          placeholder="Filter…"
                          value={(header.column.getFilterValue() as string) ?? ""}
                          onChange={(e) => header.column.setFilterValue(e.target.value)}
                          className="h-7 text-xs"
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              )}
            </thead>

            <tbody className="[&_tr:last-child]:border-0">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={headerGroups[0]?.headers.length ?? 1}
                    className="p-8 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const rowContent = (
                    <tr
                      key={row.id}
                      className={cn(
                        "border-b transition-colors hover:bg-muted/50",
                        onRowClick && "cursor-pointer",
                        rowClassName?.(row.original)
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="p-3 align-middle"
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );

                  if (renderRowWrapper) {
                    return renderRowWrapper(row.original, rowContent);
                  }
                  return rowContent;
                })
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
}
